import type { JSX } from "react";
import { useHome } from "../../controllers/useHome";
import { useNotifications } from "../../controllers/useNotifications";
import type { ProjectNavController } from "../../controllers/useProjectNav";
import type { Project } from "../../models/account";

// The shell's landing screen. The rail is a tree and the eQMS screens are all project-scoped, so
// arriving in the shell used to drop you in the app marketplace with no sense of where the work is.
// 홈 answers "what am I looking at, and what needs me?" — the workspace's projects as folders with
// their own roll-up, and the two queues addressed to this person (unread 알림, pending 검토).
export function HomeView({ nav, userName }: { nav: ProjectNavController; userName: string }) {
  const home = useHome(nav.ws.projects);
  const notifs = useNotifications();
  const projects = nav.ws.projects;
  const workspace = nav.ws.workspace;

  /** A folder card opens the project on its dashboard — the roll-up the card summarises. */
  function openProject(p: Project) {
    nav.openLeaf(p.id, { kind: "eqms", section: "dashboard" }, `${p.name} · 대시보드`);
  }

  /** Reviews and notifications point at a document, so land on it rather than on the 문서함 list. */
  function openDocument(projectId: string, documentId: string, label: string) {
    nav.openLeaf(projectId, { kind: "eqms", section: "documents" }, label);
    nav.eqms.openDetail("documents", documentId);
  }

  return (
    <div className="eqms-page home">
      <div className="home-head">
        <div>
          <h1>홈</h1>
          <p className="meta">
            {workspace ? `${workspace.name} · ` : ""}안녕하세요, {userName}님. 오늘 봐야 할 것들입니다.
          </p>
        </div>
        <button
          className="btn ghost"
          style={{ width: "auto" }}
          onClick={() => { void home.reload(); void notifs.reload(); }}
        >
          새로고침
        </button>
      </div>

      {(home.error || notifs.error) && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {home.error ?? notifs.error}
        </div>
      )}

      <div className="home-stats">
        <Tile tone="blue" label="프로젝트" value={home.totals.projects} icon={ICON.folder} />
        <Tile tone="violet" label="열린 이슈" value={home.totals.openIssues} icon={ICON.issue} />
        <Tile tone="amber" label="내 검토 대기" value={home.totals.pendingReviews} icon={ICON.review} />
        <Tile tone="rose" label="읽지 않은 알림" value={notifs.unreadCount} icon={ICON.bell} />
      </div>

      <h2 className="home-section">프로젝트</h2>
      {nav.ws.loadingWorkspace ? (
        <div className="center">불러오는 중…</div>
      ) : projects.length === 0 ? (
        <div className="home-empty">
          <p>이 워크스페이스에는 아직 프로젝트가 없습니다.</p>
          <p className="meta">왼쪽 아래 ＋ 프로젝트 생성으로 첫 프로젝트를 만들어 보세요.</p>
        </div>
      ) : (
        <div className="home-folders">
          {projects.map((p) => {
            const s = home.statsFor(p.id);
            return (
              <button key={p.id} className="folder-card" onClick={() => openProject(p)}>
                <span className="folder-top">
                  <span className="folder-ico" aria-hidden>{ICON.folder}</span>
                  <span className="folder-name">{p.name}</span>
                  {p.status === "ARCHIVED" && <span className="badge locked">보관됨</span>}
                </span>
                <span className="folder-desc">
                  {p.description?.trim() || "설명이 아직 없습니다."}
                </span>
                <span className="folder-stats">
                  <b>{s.openIssues}</b> 열린 이슈
                  <i /> <b>{s.documents}</b> 문서
                  {s.docsInReview > 0 && (
                    <>
                      <i /> <b className="warn">{s.docsInReview}</b> 검토 중
                    </>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="home-cols">
        <section className="info-card">
          <div className="home-card-head">
            <h4>읽지 않은 알림{notifs.unreadCount > 0 ? ` · ${notifs.unreadCount}` : ""}</h4>
            {notifs.unread.length > 0 && (
              <button className="link-btn" onClick={() => void notifs.markAllRead()}>모두 읽음</button>
            )}
          </div>
          {notifs.loading ? (
            <div className="center">불러오는 중…</div>
          ) : notifs.unread.length === 0 ? (
            <div className="sdui-list-empty">읽지 않은 알림이 없습니다.</div>
          ) : (
            <ul className="home-list">
              {notifs.unread.slice(0, 8).map((n) => (
                <li key={n.id}>
                  <span className="home-dot tone-rose" aria-hidden />
                  <span className="home-list-main">
                    <span className="home-list-title">{n.title || n.type}</span>
                    <span className="home-list-sub">{new Date(n.createdAt).toLocaleString()}</span>
                  </span>
                  <button className="link-btn" onClick={() => void notifs.markRead(n.id)}>읽음</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="info-card">
          <div className="home-card-head">
            <h4>내 검토 대기{home.totals.pendingReviews > 0 ? ` · ${home.totals.pendingReviews}` : ""}</h4>
          </div>
          {home.loading ? (
            <div className="center">불러오는 중…</div>
          ) : home.reviewTasks.length === 0 ? (
            <div className="sdui-list-empty">대기 중인 검토가 없습니다.</div>
          ) : (
            <ul className="home-list">
              {home.reviewTasks.slice(0, 8).map((t) => {
                const doc = home.docIndex[t.documentId];
                const role = t.role === "REVIEWER" ? "검토자" : "승인자";
                return (
                  <li key={t.id}>
                    <span className="home-dot tone-amber" aria-hidden />
                    <span className="home-list-main">
                      <span className="home-list-title">
                        {/* The inbox is account-wide, so a task can point at a document in a project
                            outside the workspace currently selected — then there is no index entry
                            and the number is all we can honestly show. */}
                        {doc ? `${doc.docNumber} ${doc.title}` : "다른 워크스페이스의 문서"}
                      </span>
                      <span className="home-list-sub">
                        {doc ? `${doc.projectName} · ${role}` : role}
                        {t.dueDate ? ` · 기한 ${new Date(t.dueDate).toLocaleDateString()}` : ""}
                      </span>
                    </span>
                    {doc && (
                      <button
                        className="link-btn"
                        onClick={() => openDocument(doc.projectId, t.documentId, `${doc.projectName} · 문서함`)}
                      >
                        열기
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

type Tone = "blue" | "violet" | "amber" | "rose";

function Tile({ tone, label, value, icon }: {
  tone: Tone; label: string; value: number; icon: JSX.Element;
}) {
  return (
    <div className={`home-tile tone-${tone}`}>
      <span className="home-tile-ico" aria-hidden>{icon}</span>
      <span>
        <span className="home-tile-label">{label}</span>
        <span className="home-tile-value">{value}</span>
      </span>
    </div>
  );
}

// One stroke set, sized by its container — the tiles and the folder cards share the folder glyph.
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const ICON = {
  folder: (
    <svg viewBox="0 0 24 24" width="19" height="19" {...stroke}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4.1a1.5 1.5 0 0 1 1.06.44L11 7.8h8.5A1.5 1.5 0 0 1 21 9.3v8.2a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
    </svg>
  ),
  issue: (
    <svg viewBox="0 0 24 24" width="19" height="19" {...stroke}>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 8v4.4l2.8 1.7" />
    </svg>
  ),
  review: (
    <svg viewBox="0 0 24 24" width="19" height="19" {...stroke}>
      <path d="M5 4.5h9.5L19 9v10.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1z" />
      <path d="M14 4.5V9h4.5" />
      <path d="m8 14.2 2.2 2.2 4-4.4" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" width="19" height="19" {...stroke}>
      <path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5S6.5 14 6.5 10z" />
      <path d="M10.4 19a1.9 1.9 0 0 0 3.2 0" />
    </svg>
  ),
};
